// Every recording on the device before the app opens (Axel 2026-10-07): which files the startup
// screen downloads. Pure: the lists in, the files to fetch out (shell/audio-preload.ts fetches them,
// shell/AudioGate.tsx shows the progress).
//
// Three kinds, each into the service worker's own runtime cache for it (vite.config.ts
// runtimeCaching), under the exact URL the players ask for, so a play (with or without Range) is
// served from the cache:
//   - Peterson's clips: /audio/<file> ("audio"), every file of the build's audio manifest
//     (data/audio-manifest.json, app/scripts/audio-manifest.ts); an Ogg file as the URL this
//     browser plays (components/audioUrl.ts: its MP3 copy where Ogg does not play). The URL has no
//     version, so the manifest's content hash says when a file changed: the hash this device
//     stored it with is remembered, and a different one fetches it again.
//   - our recordings, both of you: /api/recordings/<id>/audio[?v=<audioRev>] ("recordings"). The
//     version is in the URL: a trim or a restore is a new URL.
//   - the actors' show clips: /api/show-clips/<id>/audio?v=<version> ("show-clips"), versioned too.
// A file whose URL is already in its cache is skipped: an interrupted download (iOS ends a
// background app) carries on from what is missing at the next start.
// Asetukset → Ääni → "Äänet laitteelle" (listen/OfflineAudio.tsx) plans and downloads with the same
// code: one list of what "every recording" is, and which cache each file goes to.
import { isShowTarget } from '../showaudio/targets';

export interface AudioManifestFile {
  file: string;
  bytes: number;
  /** The first 12 hex characters of the content's SHA-1. */
  hash: string;
}

export interface AudioManifest {
  version: string;
  files: AudioManifestFile[];
}

/** The service worker's runtime caches (vite.config.ts `cacheName`s). */
export const PRELOAD_CACHE = { peterson: 'audio', ours: 'recordings', show: 'show-clips' } as const;
export type PreloadKind = keyof typeof PRELOAD_CACHE;

/** The size estimate of one of our takes before it is fetched (a short AAC/Opus clip ≈ 16 kB). */
export const OUR_TAKE_BYTES = 20_000;

export interface PreloadItem {
  kind: PreloadKind;
  /** The URL the players ask for (path and query). */
  url: string;
  cache: string;
  /** Known size (Peterson's, the show clips) or an estimate (our takes). */
  bytes: number;
  /** Peterson's files: the content hash in the manifest. */
  hash?: string;
  /** The cached copy is an older version of the file: dropped and fetched past the HTTP cache. */
  replace?: boolean;
  /** One of our takes captured from the show (target "show:…"): counted on its own line in Asetukset. */
  capture?: boolean;
}

export interface PlanInput {
  manifest: AudioManifest | null;
  /** Every recording (both people), or null when the list is not known on this device. */
  recordings: readonly { id: string; audioRev?: string; target?: string }[] | null;
  /** Every show clip, or null when the list is not known on this device. */
  showClips: readonly { id: string; version: string; bytes: number }[] | null;
  petersonUrl: (file: string) => string;
  recordingUrl: (rec: { id: string; audioRev?: string }) => string;
  showUrl: (clip: { id: string; version: string }) => string;
  /** What each cache holds: the path and query of every entry (cacheKey). */
  cached: ReadonlyMap<string, ReadonlySet<string>>;
  /** The content hash each Peterson URL had when this device stored it (by cacheKey). */
  storedHashes: Readonly<Record<string, string>>;
}

export interface PreloadPlan {
  /** Every file the device should hold. */
  wanted: number;
  /** Those files, in the order they would be fetched. */
  items: PreloadItem[];
  /** The files to fetch now, in this order. */
  todo: PreloadItem[];
  todoBytes: number;
  /** Peterson URLs already cached without a remembered hash: taken as the manifest's version. */
  adopt: Record<string, string>;
}

/** A URL as a cache entry is compared: its path and query (the origin is the app's own). */
export function cacheKey(url: string): string {
  try {
    const u = new URL(url, 'https://app.invalid');
    return u.pathname + u.search;
  } catch {
    return url;
  }
}

const COPY = /^(.+\.(?:ogg|oga))\.mp3$/i;

export function planPreload(p: PlanInput): PreloadPlan {
  const wantedItems: PreloadItem[] = [];
  const seen = new Set<string>();
  const add = (it: PreloadItem) => {
    const k = cacheKey(it.url);
    if (seen.has(k)) return;
    seen.add(k);
    wantedItems.push(it);
  };

  // our takes first: the partner's new ones are what a later start usually finds
  for (const r of p.recordings ?? []) {
    if (!r.id || r.id.startsWith('pending:')) continue; // still on this device, not on the server
    const url = p.recordingUrl(r);
    if (!url.startsWith('/')) continue; // a local object URL
    add({ kind: 'ours', url, cache: PRELOAD_CACHE.ours, bytes: OUR_TAKE_BYTES, ...(r.target && isShowTarget(r.target) ? { capture: true } : {}) });
  }

  if (p.manifest) {
    const byName = new Map(p.manifest.files.map((f) => [f.file, f]));
    for (const f of p.manifest.files) {
      // an MP3 copy of an Ogg clip is fetched only where the players ask for it instead
      const copyOf = COPY.exec(f.file)?.[1];
      if (copyOf && byName.has(copyOf)) continue;
      const url = p.petersonUrl(f.file);
      let name = f.file;
      try {
        name = decodeURIComponent(url.slice(url.lastIndexOf('/') + 1));
      } catch {
        /* keep the original's name */
      }
      const target = byName.get(name) ?? f;
      add({ kind: 'peterson', url, cache: PRELOAD_CACHE.peterson, bytes: target.bytes, hash: target.hash });
    }
  }

  for (const c of p.showClips ?? []) {
    if (!c.id || !c.version) continue;
    add({ kind: 'show', url: p.showUrl(c), cache: PRELOAD_CACHE.show, bytes: Math.max(0, c.bytes || 0) });
  }

  const todo: PreloadItem[] = [];
  const adopt: Record<string, string> = {};
  for (const it of wantedItems) {
    const k = cacheKey(it.url);
    const have = p.cached.get(it.cache)?.has(k) ?? false;
    if (!have) {
      todo.push(it);
      continue;
    }
    if (it.kind !== 'peterson' || !it.hash) continue;
    const stored = p.storedHashes[k];
    if (stored === undefined) adopt[k] = it.hash;
    else if (stored !== it.hash) todo.push({ ...it, replace: true });
  }
  return { wanted: wantedItems.length, items: wantedItems, todo, todoBytes: todo.reduce((s, x) => s + x.bytes, 0), adopt };
}

/** The lines of Asetukset → "Äänet laitteelle": Peterson's, our takes, our show captures, the actors' clips. */
export type OfflineGroup = 'peterson' | 'ours' | 'captures' | 'show';

const groupOf = (it: PreloadItem): OfflineGroup => (it.kind === 'ours' && it.capture ? 'captures' : it.kind);

/** Per line: how many files the device should hold, and how many it holds in their current version. */
export function planGroups(plan: Pick<PreloadPlan, 'items' | 'todo'>): Record<OfflineGroup, { wanted: number; have: number }> {
  const out: Record<OfflineGroup, { wanted: number; have: number }> = {
    peterson: { wanted: 0, have: 0 },
    ours: { wanted: 0, have: 0 },
    captures: { wanted: 0, have: 0 },
    show: { wanted: 0, have: 0 },
  };
  for (const it of plan.items) {
    out[groupOf(it)].wanted++;
    out[groupOf(it)].have++;
  }
  for (const it of plan.todo) out[groupOf(it)].have--;
  return out;
}

/**
 * localStorage switch: 'off' turns the startup download off on this device (the load measurements,
 * e2e/perf.mjs); 'on' turns it on in a dev build (it runs in production builds only otherwise).
 */
export const SWITCH_KEY = 'valyrio:audioPreload';

/** Whether this page downloads the audio at start (production, Cache Storage, not switched off). */
export function preloadEnabled(prod: boolean = import.meta.env.PROD): boolean {
  if (typeof caches === 'undefined' || typeof fetch === 'undefined') return false;
  let s: string | null = null;
  try {
    s = localStorage.getItem(SWITCH_KEY);
  } catch {
    /* no storage: the default */
  }
  return prod ? s !== 'off' : s === 'on';
}

/** Below this much the startup screen is not shown at once (a few of the partner's new takes). */
export const QUIET_BYTES = 1_000_000;
export const QUIET_FILES = 30;
/** A quiet download still running after this long shows the startup screen after all. */
export const QUIET_SHOW_AFTER_MS = 800;

export type PreloadStart = 'open' | 'quiet' | 'block';

/**
 * Open the app at once (nothing missing, or offline: never block without a network), fetch quietly
 * (a little), or hold the startup screen until the download is done.
 */
export function preloadStart(plan: Pick<PreloadPlan, 'todo' | 'todoBytes'>, online: boolean): PreloadStart {
  if (!plan.todo.length || !online) return 'open';
  return plan.todoBytes < QUIET_BYTES && plan.todo.length <= QUIET_FILES ? 'quiet' : 'block';
}

/** Room the download needs beyond its own size (the dictionary, the form index, growth). */
export const STORAGE_MARGIN = 25_000_000;

/** False when the browser says the files will not fit (unknown: assume they do). */
export function fits(todoBytes: number, est: { usage?: number; quota?: number }): boolean {
  if (est.usage === undefined || est.quota === undefined || !(est.quota > 0)) return true;
  return est.quota - est.usage >= todoBytes + STORAGE_MARGIN;
}
